import { Link } from 'react-router-dom';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import { SectionCard } from '@/components/common/SectionCard';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { clearPurchaseIntent } from '@/lib/auth/intent-storage';
import { ROUTES } from '@/lib/constants/routes';
import { DATA_ROOM_STRINGS } from '../constants';
import type { JourneyStep } from '../issue-report-journey';

const COPY = DATA_ROOM_STRINGS.JOURNEY;

/** The steps that stop on a screen rather than redirect. */
export type JourneyStopKind = Extract<
  JourneyStep['kind'],
  'loading' | 'error' | 'coming_soon' | 'admin_only'
>;

export interface IssueReportJourneyStateProps {
  kind: JourneyStopKind;
  /** Runs the failed request again; only the `error` state offers it. */
  onRetry: () => void;
}

/**
 * The screens of the Issue Report journey that do not redirect (FR-204,
 * FR-205): loading, a failed offer or access read, Coming Soon and "Only a
 * Startup Admin can buy". The startup step has its own screen (FR-225). None of them opens
 * a checkout. The way back forgets the purchase intent, so a later login lands
 * normally (FR-206).
 */
export function IssueReportJourneyState({ kind, onRetry }: IssueReportJourneyStateProps) {
  if (kind === 'loading') return <LoadingSpinner label={COPY.LOADING} />;

  const back =
    kind === 'admin_only'
      ? { to: ROUTES.DASHBOARD, label: COPY.BACK_TO_DASHBOARD }
      : { to: ROUTES.DUE_DILIGENCE_PLATFORM, label: COPY.BACK_TO_DUE_DILIGENCE };
  const copy = {
    error: COPY.OFFER_ERROR,
    coming_soon: COPY.COMING_SOON,
    admin_only: COPY.ADMIN_ONLY,
  }[kind];

  return (
    <SectionCard>
      <div className="mx-auto flex max-w-xl flex-col items-center gap-3 py-6 text-center" role="status">
        <h2 className="text-h3 text-navy">{copy.title}</h2>
        <p className="text-text-secondary text-sm">{copy.body}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          {kind === 'coming_soon' && <ComingSoonBadge />}
          {kind === 'error' && <Button onClick={onRetry}>{COPY.OFFER_ERROR.action}</Button>}
          <Button asChild variant="outline">
            <Link to={back.to} onClick={clearPurchaseIntent}>
              {back.label}
            </Link>
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
